import React from 'react';
import {
    selectCapsuleSymbolBindingFromState,
    WidgetPanelDetailProps,
} from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';

import { toHistPriceVolRequestUid } from 'widgets/historicalPercentageChange/store/data/HistoricalPercentageChangeStreamRequest.ts';
import {
    ChartDataItem,
    computeChartData,
} from 'widgets/historicalPercentageChange/components/HistoricalPercentageChangeChart/chartData.ts';
import { WidgetPanelVolumeCapsuleProps } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelVolumeCapsule.tsx';
import { buildHistoricalPercentageChangeStoreSlice } from 'widgets/historicalPercentageChange/store/slice.ts';
import { WidgetType } from 'widgets/WidgetType.ts';
import { initialHistoricalPercentageChangeCapsuleConfigState } from 'widgets/historicalPercentageChange/capsule/store.ts';
import { mapToHistoricalPercentageChangeDataItems } from 'widgets/historicalPercentageChange/data/useHistoricalPercentageChangeData.ts';
import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid.tsx';
import { HistPriceVolDataItem } from 'widgets/historicalPercentageChange/store/data/HistoricalPercentageChangeDataState.ts';

const {
    configSlice: {
        selectors: { selectConfigState },
    },
} = buildHistoricalPercentageChangeStoreSlice(
    WidgetType.HistoricalPercentageChangeCapsule,
    initialHistoricalPercentageChangeCapsuleConfigState
);

export interface WidgetPanelHistoricalPercentageChangeProps
    extends WidgetPanelDetailProps {
    mode: 'streamData' | 'data';
}

export default function WidgetPanelHistoricalPercentageChange(
    props: WidgetPanelVolumeCapsuleProps
) {
    const { mode, state } = props;

    const symbolBinding = selectCapsuleSymbolBindingFromState(props);

    if (!symbolBinding) {
        return <></>;
    }

    const { symbol, source } = symbolBinding;

    const { periodQty } = selectConfigState(state.state, props.widget.id);

    const requestUid = toHistPriceVolRequestUid(symbol);
    const streamData = state.state.streams.data.histPriceVol[requestUid]?.data;
    const items = mapToHistoricalPercentageChangeDataItems(streamData);
    const data = computeChartData(items, periodQty);

    if (mode === 'streamData') {
        return (
            <WidgetPanelGrid
                data={streamData?.data ?? []}
                getRowDataId={(d: HistPriceVolDataItem) => d.index.toString()}
                csvFileName={'histPercVol.csv'}
            />
        );
    }

    return (
        <div className="widget-panel max-grid">
            <div className="title">
                Historical Percentage Widget/Capsule Data
            </div>
            <div className="field">
                <span className={'label'}>Symbol:</span>
                {symbol}
            </div>
            <div className="field">
                <span className={'label'}>Source:</span>
                {source}
            </div>
            <div className="field">
                <span className={'label'}>Period Qty</span>
                {periodQty}
            </div>
            <div className="field">
                <span className={'label'}>Average Volume</span>
                {data.averageVolume}
            </div>

            <WidgetPanelGrid
                data={data.chartData ?? []}
                getRowDataId={(d: ChartDataItem) => d.xIndex.toString()}
                csvFileName={'histPercVolChartData.csv'}
            />
        </div>
    );
}

export function addHistoricalPercentagePanes(
    detailProps: WidgetPanelDetailProps,
    panes: { menuItem: string; render: () => JSX.Element }[]
) {
    const render = () => (
        <WidgetPanelHistoricalPercentageChange {...detailProps} mode="data" />
    );
    panes.push({
        menuItem: 'Chart Data',
        render,
    });

    const renderTs = () => (
        <WidgetPanelHistoricalPercentageChange
            {...detailProps}
            mode="streamData"
        />
    );
    panes.push({
        menuItem: 'Stream',
        render: renderTs,
    });
}
